import React, { useState, useMemo, useCallback } from "react";
import Table from "../../../shared/components/common/Table";
import SearchBar from "../../../shared/components/common/SearchBar";
import TableToolbar from "../../../shared/components/common/TableToolbar";
import { getCountryFromNumber } from "../constants/data";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isViewer } from "../../../shared/utils/roles";

const AvailableNumbers = ({
  numbers,
  onBuy,
  spreadTable,
  onSpreadTableToggle,
  loading = false,
  pageSize = 10,
  canEdit = true,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const viewer = isViewer(currentUser?.user_role);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [numberFilter, setNumberFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const typeOptions = useMemo(() => {
    const unique = [
      ...new Set((numbers || []).map((n) => n.type).filter(Boolean)),
    ];
    return [
      { value: "all", name: "All Types" },
      ...unique.map((t) => ({ value: t, name: t })),
    ];
  }, [numbers]);

  const filteredNumbers = useMemo(() => {
    let result = numbers || [];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (n) =>
          n.number.toLowerCase().includes(lower) ||
          n.type.toLowerCase().includes(lower),
      );
    }
    if (numberFilter) {
      const lower = numberFilter.toLowerCase();
      result = result.filter((n) => n.number.toLowerCase().includes(lower));
    }
    if (typeFilter && typeFilter !== "all") {
      result = result.filter((n) => n.type === typeFilter);
    }
    return result;
  }, [numbers, searchTerm, numberFilter, typeFilter]);

  const hasAnyNumbers = (numbers || []).length > 0;

  const filterFields = useMemo(
    () => [
      {
        key: "number",
        label: "Number",
        type: "text",
        value: numberFilter,
        onChange: setNumberFilter,
        placeholder: "Filter by number",
      },
      {
        key: "type",
        label: "Type",
        type: "select",
        value: typeFilter,
        onChange: setTypeFilter,
        options: typeOptions,
      },
    ],
    [numberFilter, typeFilter, typeOptions],
  );

  const columns = useMemo(() => {
    const cols = [
      // disabled + hidden checkbox column
      {
        Header: () => (
          <input
            type="checkbox"
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary"
            disabled
            style={{
              display: "none",
            }}
          />
        ),
        accessor: "checkbox",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary"
          />
        ),
      },
      {
        Header: "NUMBER",
        accessor: "number",
        Cell: ({ value }) => (
          <div className="flex flex-col">
            <span className="text-content text-sm font-medium">{value}</span>
            <span className="text-gray-500 text-xs">
              {getCountryFromNumber(value)}
            </span>
          </div>
        ),
      },
      {
        Header: "TYPE",
        accessor: "type",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
      {
        Header: "MONTHLY FEE",
        accessor: "monthlyFee",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
    ];

    if (!viewer) {
      cols.push({
        Header: "ACTION",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            onClick={() => onBuy(row.original)}
            disabled={!canEdit}
            className="px-5 py-1.5 bg-primary text-white rounded-lg text-xs font-bold tracking-wider hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Buy
          </button>
        ),
      })
    }

    return cols;
  }, [onBuy, viewer, canEdit]);

  return (
    <div className="mb-8">
      <h2 className="text-base font-bold text-primary sm:mx-2 mb-3">
        Available Numbers
      </h2>
      <div
        className={`sm:mx-2 border border-line rounded-t-xl shadow-sm flex flex-col logs-table-area ${
          spreadTable ? " table-zoom-out" : ""
        }`}
      >
        <div className="p-4 bg-white border-b border-line flex max-[400px]:flex-col flex-row justify-between sm:items-center rounded-t-xl max-[400px]:gap-4 gap-2 sm:gap-0">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search number"
            useMUIicons={false}
          />
          <TableToolbar
            spreadTable={spreadTable}
            onSpreadTableToggle={onSpreadTableToggle}
            displayData={filteredNumbers}
            columns={columns}
            filterFields={filterFields}
          />
        </div>
        {!loading && filteredNumbers.length === 0 ? (
          <div className="w-full px-6 py-10 bg-white rounded-b-xl text-center">
            <p className="text-sm font-semibold text-primary mb-1">
              No phone numbers available
            </p>
            <p className="text-xs text-gray-500">
              {hasAnyNumbers
                ? "No numbers match your current filters. Try adjusting your search criteria."
                : "Run a search to view available phone numbers."}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <Table
              columns={columns}
              data={filteredNumbers}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              rowIdKey="id"
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableNumbers;
